import React, { useEffect, useRef } from 'react';
import Icon from './icons/Icon';
import { Habit } from '../data/habitData';
import { isShoppingHabitId } from '../utils/habits';
import './InputTodoTitle.css'; 
// ↑ 今は既存CSSを共用。後でCSSを分離します。

export interface InputTodoTitleProps {
  /** 現在のタイトル文字列（親の状態を表示） */
  title: string;
  /** 適用中の習慣ID（ある場合はタイトル編集をロック） */
  habitId?: string;
  /** 候補として表示する習慣一覧 */
  activeHabits: Habit[];

  /** 入力中の変化（親でローカル状態更新＋必要ならデバウンス保存） */
  onTitleTyping: (value: string) => void;
  /** 文字確定（IME確定・確定的操作時。親で即時保存） */
  onTitleCommit: (value: string) => void;

  /** 習慣が選択された（親で title＆habitId を更新して即時保存） */
  onHabitSelect: (habit: Habit) => void;
  /** 習慣を解除（親で habitId を外し、表示中タイトルをテキストとして保持） */
  onHabitClear: (currentTitle: string) => void;
  /** 「◯◯ を追加」で新規習慣作成を親に依頼する */
  onAddNewHabit?: (title: string) => void;

  /** 親で IME 中フラグを持続させるための通知（保存戦略のため） */
  onCompositionStart?: () => void;
  onCompositionEnd?: (value: string) => void;
}

const MIN_TITLE_HEIGHT = 48;

const InputTodoTitle: React.FC<InputTodoTitleProps> = ({
  title,
  habitId,
  activeHabits,
  onTitleTyping,
  onTitleCommit,
  onHabitSelect,
  onHabitClear,
  onAddNewHabit,
  onCompositionStart,
  onCompositionEnd,
}) => {
  // タイトル入力のDOM参照（高さの自動調整用）
  const titleInputRef = useRef<HTMLTextAreaElement>(null);

  // UI内部状態（ドロップダウンの開閉・検索語）
  const [showHabitDropdown, setShowHabitDropdown] = React.useState(false);
  const [habitSearchTerm, setHabitSearchTerm] = React.useState('');
  const [filteredHabits, setFilteredHabits] = React.useState<Habit[]>([]);
  const isHabitSelected = !!habitId;

  // 高さの自動調整（入力のたびに内容に合わせる）
  const autoResizeTitle = () => {
    const el = titleInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.max(MIN_TITLE_HEIGHT, el.scrollHeight);
    el.style.height = `${next}px`;
  };

  // 初期＆依存変化で高さを同期
  useEffect(() => { autoResizeTitle(); }, []);
  useEffect(() => { autoResizeTitle(); }, [title, isHabitSelected]);

  // 習慣フィルタリング（検索語が変わるたびに再計算）
  useEffect(() => {
    let filtered = activeHabits;
    
    if (habitSearchTerm.trim() !== '') {
      const lower = habitSearchTerm.toLowerCase();
      filtered = activeHabits.filter(h => h.title.toLowerCase().includes(lower));
    }
    
    // 「買い物」を最優先で並び替え
    const shoppingHabit = filtered.find(h => isShoppingHabitId(h.id));
    const otherHabits = filtered.filter(h => !isShoppingHabitId(h.id));
    
    if (shoppingHabit) {
      setFilteredHabits([shoppingHabit, ...otherHabits]);
    } else {
      setFilteredHabits(otherHabits);
    }
  }, [habitSearchTerm, activeHabits]);

  // 入力時（編集中の値を親に伝えてローカル状態や保存ステータスを更新してもらう）
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (isHabitSelected) return; // 習慣適用中は編集ロック
    const v = e.target.value;
    setHabitSearchTerm(v);       // ドロップダウンの候補フィルタにも利用
    onTitleTyping(v);            // 親：ローカル状態更新＋必要ならデバウンス保存
    requestAnimationFrame(autoResizeTitle); // 高さ再計算
  };

  // IME開始/終了（親へ通知。終了時は確定値で即時保存トリガー）
  const handleCompositionStart = () => { onCompositionStart?.(); };
  const handleCompositionEnd = (e: React.CompositionEvent<HTMLTextAreaElement>) => {
    const v = e.currentTarget.value;
    setHabitSearchTerm(v);
    // 確定文字を親へ伝えて即時保存をトリガー
    onCompositionEnd?.(v);
    onTitleCommit(v);
    // 編集中は候補を出しっぱなしにしたい場合は必要に応じて開く
    setShowHabitDropdown(true);
    requestAnimationFrame(autoResizeTitle);
  };

  // フォーカス時は候補を常に表示（「◯◯を追加」導線も見せる）
  const handleFocus = () => {
    if (!isHabitSelected) {
      setHabitSearchTerm(title);
      setShowHabitDropdown(true);
    }
  };

  // 少し遅延してから閉じる（ドロップダウンのクリックを拾うため）
  const handleBlur = () => {
    setTimeout(() => setShowHabitDropdown(false), 200);
  };

  // Enterでの確定（単一行想定のUIなので改行は抑制）
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      setShowHabitDropdown(true);
    }
  };

  // 習慣選択
  const selectHabit = (habit: Habit) => {
    onHabitSelect(habit);        // 親：title & habitId を更新して即時保存
    setHabitSearchTerm('');      // 検索語はクリア
    setShowHabitDropdown(false); // ドロップダウンを閉じる
  };

  // 習慣解除（現在の表示テキストをタイトルとして残す）
  const clearHabit = () => {
    const currentTitle = title;  // 表示中タイトルを親に渡す
    onHabitClear(currentTitle);  // 親：habitId を外し、titleは据え置きで即時保存
    setHabitSearchTerm(currentTitle);
    requestAnimationFrame(autoResizeTitle);
    titleInputRef.current?.focus();
  };

  // 表示時は、習慣が選択されていれば常に習慣名を優先表示
  const displayTitle = React.useMemo(() => {
    if (isHabitSelected && habitId) {
      const h = activeHabits.find(h => h.id === habitId);
      return h?.title ?? title;
    }
    return title;
  }, [isHabitSelected, habitId, activeHabits, title]);

  return (
    <div className="input-todo-title__container">
      {/* 習慣アイコン（習慣選択中のみ表示） */}
      {isHabitSelected && (
        <div className="input-todo-title__habit-icon">
          <Icon name={isShoppingHabitId(habitId) ? 'shop' : 'habit'} size={24} />
        </div>
      )}

      <textarea
        ref={titleInputRef}
        className={`input-todo-title__textarea text-body-large ${
          isHabitSelected ? 'input-todo-title__textarea--readonly' : ''
        } ${isHabitSelected ? 'input-todo-title__textarea--with-habit-icon' : ''}`}
        value={displayTitle}
        onChange={handleChange}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="Todo のタイトルを入力"
        readOnly={isHabitSelected}
        rows={1}
      />

      {/* 習慣解除ボタン（選択中のみ） */}
      {isHabitSelected && (
        <button
          type="button"
          className="input-todo-title__habit-clear-button"
          onClick={clearHabit}
          aria-label="習慣選択を解除"
        >
          <Icon name="close" size={16} />
        </button>
      )}

      {/* 習慣候補ドロップダウン（未選択時のみ） */}
      {showHabitDropdown && !isHabitSelected && (
        <div className="input-todo-title__habit-dropdown">
          {filteredHabits.map(habit => (
            <button
              key={habit.id}
              type="button"
              className="input-todo-title__habit-item text-body-medium"
              onClick={() => selectHabit(habit)}
            >
              <Icon name={isShoppingHabitId(habit.id) ? 'shop' : 'habit'} size={16} />
              <span>{habit.title}</span>
            </button>
          ))}
          {/* 入力があれば「◯◯ を追加」ボタンを表示（作成自体は親が担うなら親コールバックを追加してもOK） */}
          {habitSearchTerm.trim() !== '' && (
            <button
              type="button"
              className="input-todo-title__habit-add-new text-body-medium"
              onClick={() => {
                const t = habitSearchTerm.trim();
                if (t) {
                  onAddNewHabit?.(t); // 親に新規作成を依頼（重複チェックや作成は親で実施）
                }
                setShowHabitDropdown(false); // ドロップダウンを閉じる
                setHabitSearchTerm('');      // 検索語をクリア
              }}
            >
              <Icon name="add" size={16} />
              <span>{habitSearchTerm} を習慣に追加</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default InputTodoTitle;
